import React, { useState } from "react";
import {
  CATEGORY_CONFIG,
  getSectionsByCategory,
  searchSections,
} from "../../data/documentationSections";

/**
 * Modern Table of Contents Component with Search
 */
const DocNav = ({ darkMode = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const sectionsByCategory = getSectionsByCategory();
  const filteredSections = searchQuery ? searchSections(searchQuery) : null;

  const theme = darkMode
    ? {
        background: "#0f172a",
        cardBg: "#1e293b",
        border: "#334155",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        searchBg: "#1e293b",
        searchBorder: "#475569",
      }
    : {
        background: "#f8fafc",
        cardBg: "#ffffff",
        border: "#e2e8f0",
        text: "#1e293b",
        textSecondary: "#64748b",
        searchBg: "#ffffff",
        searchBorder: "#d1d5db",
      };

  const renderCategorySection = (categoryKey, sections) => {
    const config = CATEGORY_CONFIG[categoryKey];
    if (!sections || sections.length === 0) return null;

    return (
      <div
        key={categoryKey}
        style={{
          backgroundColor: theme.cardBg,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: darkMode
            ? "0 4px 20px rgba(0, 0, 0, 0.3)"
            : "0 4px 20px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.border}`,
        }}
      >
        <h3
          style={{
            fontSize: "1.4rem",
            fontWeight: "700",
            color: config.color,
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {config.title}
        </h3>
        <p
          style={{
            fontSize: "0.9rem",
            color: theme.textSecondary,
            marginBottom: "20px",
          }}
        >
          {config.description}
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 16px",
                backgroundColor: config.bgColor,
                borderRadius: "8px",
                textDecoration: "none",
                color: config.textColor,
                fontWeight: "500",
                transition: "all 0.3s ease",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateX(4px)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateX(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <span style={{ fontSize: "1.2rem" }}>{section.emoji}</span>
              <span>{section.title}</span>
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: theme.background,
        padding: "80px 20px",
        borderBottom: `1px solid ${theme.border}`,
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "700",
              color: theme.text,
              marginBottom: "16px",
            }}
          >
            📖 Documentation Contents
          </h2>
          <p
            style={{
              fontSize: "1.2rem",
              color: theme.textSecondary,
              maxWidth: "600px",
              margin: "0 auto 32px auto",
            }}
          >
            Navigate through our comprehensive guide
          </p>

          {/* Search Bar */}
          <div
            style={{
              maxWidth: "600px",
              margin: "0 auto",
              position: "relative",
            }}
          >
            <input
              type="text"
              placeholder="🔍 Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "16px 20px",
                fontSize: "1rem",
                borderRadius: "12px",
                border: `2px solid ${theme.searchBorder}`,
                backgroundColor: theme.searchBg,
                color: theme.text,
                outline: "none",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = theme.searchBorder;
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  color: theme.textSecondary,
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Search Results or Categories */}
        {filteredSections ? (
          <div>
            <p
              style={{
                textAlign: "center",
                color: theme.textSecondary,
                marginBottom: "24px",
              }}
            >
              Found {filteredSections.length} result(s)
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "20px",
              }}
            >
              {Object.entries(
                filteredSections.reduce((acc, section) => {
                  if (!acc[section.category]) acc[section.category] = [];
                  acc[section.category].push(section);
                  return acc;
                }, {})
              ).map(([category, sections]) =>
                renderCategorySection(category, sections)
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "20px",
            }}
          >
            {Object.entries(sectionsByCategory).map(([category, sections]) =>
              renderCategorySection(category, sections)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocNav;
